import type { FilterOptionsState } from '@mui/material/useAutocomplete';

export interface TimezoneOption {
  label: string;
  value: string;
}

/** Labels use current DST offsets when the module loads. */
const TIMEZONE_LABEL_REF_DATE = new Date();

/**
 * Canonical IANA zones from ICU/CLDR via ECMA-402 (`Intl.supportedValuesOf`).
 * This curated set omits `Etc/*` link targets and legacy synonyms like `Etc/UTC`.
 */
export function getSelectableTimezoneIds(): string[] {
  const ids = new Set(Intl.supportedValuesOf('timeZone'));

  // API schedules use `UTC`; it is not always present in supportedValuesOf.
  ids.add('UTC');

  const browserTz = getBrowserTimezone();
  if (browserTz && !ids.has(browserTz) && isValidTimezoneId(browserTz)) {
    ids.add(browserTz);
  }

  return [...ids];
}

function isValidTimezoneId(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(TIMEZONE_LABEL_REF_DATE);
    return true;
  } catch {
    return false;
  }
}

/** Format `GMT±HH:MM` for a zone at a reference instant (DST-aware). */
export function formatGmtPrefix(timeZone: string, refDate: Date = TIMEZONE_LABEL_REF_DATE): string {
  if (timeZone === 'UTC') {
    return 'GMT+00:00';
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset',
  }).formatToParts(refDate);
  const raw = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) {
    return 'GMT+00:00';
  }

  const sign = match[1];
  const hours = match[2].padStart(2, '0');
  const minutes = (match[3] ?? '00').padStart(2, '0');
  return `GMT${sign}${hours}:${minutes}`;
}

export function formatTimezoneLabel(
  timeZone: string,
  refDate: Date = TIMEZONE_LABEL_REF_DATE
): string {
  return `(${formatGmtPrefix(timeZone, refDate)}) ${timeZone}`;
}

function buildTimezoneOption(timeZone: string): TimezoneOption {
  return { label: formatTimezoneLabel(timeZone), value: timeZone };
}

/** Canonical selectable zones with generated labels. */
export const ALL_TIMEZONES: TimezoneOption[] = getSelectableTimezoneIds().map(buildTimezoneOption);

const SORTED_TIMEZONE_OPTIONS = sortTimezonesByOffset(ALL_TIMEZONES);

/**
 * Resolve a stored timezone id to a dropdown option.
 * Falls back to a generated label for legacy aliases (e.g. Asia/Kolkata) not in the curated list.
 */
export function resolveTimezoneOption(
  value: string | undefined | null,
  options: TimezoneOption[]
): TimezoneOption | null {
  if (!value) {
    return null;
  }

  const direct = options.find((option) => option.value === value);
  if (direct) {
    return direct;
  }

  if (!isValidTimezoneId(value)) {
    return { label: value, value };
  }

  return buildTimezoneOption(value);
}

/** Parse `(GMT±HH:MM)` from a timezone label into signed offset minutes. */
export function parseGmtOffsetMinutes(label: string): number {
  const match = label.match(/\(GMT([+-])(\d{2}):(\d{2})\)/);
  if (!match) {
    return 0;
  }
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

/** Sort by UTC offset (west → east), then IANA id. Divider rows (empty value) are kept in place. */
export function sortTimezonesByOffset(options: TimezoneOption[]): TimezoneOption[] {
  const dividers = options.filter((opt) => opt.value === '');
  if (dividers.length > 0) {
    const sortable = options.filter((opt) => opt.value !== '');
    return [...sortTimezonesByOffset(sortable), ...dividers];
  }

  return [...options].sort((a, b) => {
    const offsetDiff = parseGmtOffsetMinutes(a.label) - parseGmtOffsetMinutes(b.label);
    if (offsetDiff !== 0) {
      return offsetDiff;
    }
    return a.value.localeCompare(b.value);
  });
}

/** Preserve dropdown order while filtering; MUI Autocomplete sorts alphabetically by default. */
export function filterTimezoneOptions(
  options: TimezoneOption[],
  state: FilterOptionsState<TimezoneOption>
): TimezoneOption[] {
  const query = state.inputValue.trim().toLowerCase();
  if (!query) {
    return options;
  }
  return options.filter(
    (opt) =>
      opt.value === '' ||
      opt.label.toLowerCase().includes(query) ||
      opt.value.toLowerCase().includes(query)
  );
}

/**
 * Get the browser's timezone using Intl API
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * All timezones sorted west → east by GMT offset (e.g. -11:00 through +14:00).
 * Browser timezone is pinned at the top with "(Local)" when present in the list.
 */
export function getTimezoneOptions(): TimezoneOption[] {
  const browserTz = getBrowserTimezone();
  const browserEntry = resolveTimezoneOption(browserTz, SORTED_TIMEZONE_OPTIONS);

  if (!browserEntry) {
    return SORTED_TIMEZONE_OPTIONS;
  }

  return [
    { label: `${browserEntry.label.trim()} (Local)`, value: browserEntry.value },
    ...SORTED_TIMEZONE_OPTIONS.filter((t) => t.value !== browserEntry.value),
  ];
}

/**
 * Get timezone label by value
 */
export function getTimezoneLabel(value: string): string {
  if (!isValidTimezoneId(value)) {
    return value;
  }
  return formatTimezoneLabel(value);
}

// ---------------------------------------------------------------------------
// Timezone ↔ UTC conversion helpers
// ---------------------------------------------------------------------------

interface TimeParts {
  hour: number;
  minute: number;
}

/** Extract hour/minute for an instant in an IANA timezone. */
function getTimePartsInZone(date: Date, timeZone: string): TimeParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10) % 24;
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  return { hour, minute };
}

/**
 * Find the UTC instant when the given local wall-clock time occurs in timeZone on a calendar day.
 * Uses the reference date's DST rules for that timezone.
 */
const MS_PER_CALENDAR_DAY = 86_400_000;

function calendarUtcDayIndex(year: number, month: number, day: number): number {
  return Date.UTC(year, month - 1, day) / MS_PER_CALENDAR_DAY;
}

/** Convert wall-clock components in an IANA zone to a UTC instant (DST-aware). */
export function wallClockInTimeZoneToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  let guess = Date.UTC(year, month - 1, day, hour, minute, 0);

  for (let i = 0; i < 4; i++) {
    const d = new Date(guess);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const p = Object.fromEntries(
      fmt
        .formatToParts(d)
        .filter((x) => x.type !== 'literal')
        .map((x) => [x.type, parseInt(x.value, 10)])
    ) as Record<string, number>;

    const localMinutes = (p.hour % 24) * 60 + p.minute;
    const targetMinutes = hour * 60 + minute;
    const dayDiff =
      calendarUtcDayIndex(year, month, day) - calendarUtcDayIndex(p.year, p.month, p.day);
    const diffMinutes = targetMinutes - localMinutes + dayDiff * 1440;

    if (diffMinutes === 0) {
      break;
    }
    guess += diffMinutes * 60_000;
  }

  return new Date(guess);
}

/**
 * Map a local wall-clock hour (:00) to the UTC hour used in EventBridge cron fields.
 * Uses the same DST-aware conversion as daily/repeating schedules.
 */
export function localWallClockHourToUtcHour(
  localHour: number,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): number {
  return convertLocalTimeToUTC(localHour, 0, timeZone, refDate).hour;
}

/**
 * Inverse of {@link localWallClockHourToUtcHour} for cron hour fields.
 * Ensures edit-form time-frame dropdowns round-trip after UTC storage.
 */
export function utcHourToLocalWallClockHour(
  utcHour: number,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): number {
  for (let localHour = 0; localHour < 24; localHour++) {
    if (localWallClockHourToUtcHour(localHour, timeZone, refDate) === utcHour) {
      return localHour;
    }
  }

  return convertUTCToLocalTime(utcHour, 0, timeZone, refDate).hour;
}

/**
 * Convert a local hour:minute in a specific timezone to UTC hour:minute.
 * Uses the reference date's DST offset for that timezone.
 *
 * Example: 09:00 in Asia/Kolkata (UTC+5:30) → 03:30 UTC
 */
export function convertLocalTimeToUTC(
  hour: number,
  minute: number,
  timeZone: string,
  refDate: Date = new Date()
): { hour: number; minute: number } {
  const utc = wallClockInTimeZoneToUtc(
    refDate.getFullYear(),
    refDate.getMonth() + 1,
    refDate.getDate(),
    hour,
    minute,
    timeZone
  );
  return { hour: utc.getUTCHours(), minute: utc.getUTCMinutes() };
}

/**
 * Convert a UTC hour:minute to local hour:minute in a specific timezone.
 * Uses the reference date's DST offset for that timezone.
 *
 * Example: 03:30 UTC → 09:00 in Asia/Kolkata (UTC+5:30)
 */
export function convertUTCToLocalTime(
  utcHour: number,
  utcMinute: number,
  timeZone: string,
  refDate: Date = new Date()
): { hour: number; minute: number } {
  const utc = new Date(
    Date.UTC(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), utcHour, utcMinute, 0)
  );
  return getTimePartsInZone(utc, timeZone);
}

interface DateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function getDateTimePartsInZone(date: Date, timeZone: string): DateTimeParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, parseInt(part.value, 10)])
  ) as Record<string, number>;

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour % 24,
    minute: parts.minute,
  };
}

/** Local wall-clock date+time in IANA zone → UTC cron field components (fixed-time schedules). */
export function convertLocalWallClockScheduleToUtcParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): { utcMinute: number; utcHour: number; utcDay: number; utcMonth: number; utcYear: number } {
  const utc = wallClockInTimeZoneToUtc(year, month, day, hour, minute, timeZone);
  return {
    utcMinute: utc.getUTCMinutes(),
    utcHour: utc.getUTCHours(),
    utcDay: utc.getUTCDate(),
    utcMonth: utc.getUTCMonth() + 1,
    utcYear: utc.getUTCFullYear(),
  };
}

/** UTC cron field components → local wall-clock date+time in IANA zone (fixed-time edit load). */
export function convertUtcCronInstantToLocalWallClockSchedule(
  utcYear: number,
  utcMonth: number,
  utcDay: number,
  utcHour: number,
  utcMinute: number,
  timeZone: string
): DateTimeParts {
  const utc = new Date(Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, utcMinute, 0));
  return getDateTimePartsInZone(utc, timeZone);
}
